<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Plus, RefreshCw } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import ListPagination from '../components/ListPagination.vue'
import { PURCHASE_ORDER_STATUSES, purchaseOrderStatusClass } from '../types/procurement'
import type { AgentOption, PurchaseOrder } from '../types/procurement'

const { t } = useI18n()
const auth = useAuthStore()
const api = useProcurementApi()

const canCreate = computed(() => (auth.user?.permissions ?? []).includes('purchase_orders.create'))

const orders = ref<PurchaseOrder[]>([])
const suppliers = ref<AgentOption[]>([])
const warehouses = ref<AgentOption[]>([])
const loading = ref(true)
const error = ref('')

const search = ref('')
const statusFilter = ref('')
const supplierFilter = ref('')
const warehouseFilter = ref('')

const page = ref(1)
const perPage = ref(20)
const total = ref(0)
const totalPages = ref(1)

const statuses = PURCHASE_ORDER_STATUSES

let searchTimer: ReturnType<typeof setTimeout> | undefined

/** `GET /purchase-orders` is paged server-side and returns a `meta` block. */
async function loadOrders() {
  const result = await api.listPurchaseOrders({
    search: search.value.trim() || undefined,
    status: statusFilter.value || undefined,
    supplier_agent_id: supplierFilter.value ? Number(supplierFilter.value) : undefined,
    destination_warehouse_agent_id: warehouseFilter.value ? Number(warehouseFilter.value) : undefined,
    page: page.value,
    per_page: perPage.value,
  })

  orders.value = result.items
  total.value = result.meta.total
  totalPages.value = Math.max(1, result.meta.total_pages)
}

async function reload() {
  page.value = 1
  await loadOrders().catch((cause) => {
    error.value = formatApiError(cause)
  })
}

function changePage(next: number) {
  page.value = next
  void loadOrders().catch((cause) => {
    error.value = formatApiError(cause)
  })
}

function changePerPage(size: number) {
  perPage.value = size
  void reload()
}

watch(search, () => {
  // Debounce typing so the register is not queried on every keystroke.
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => void reload(), 300)
})

watch([statusFilter, supplierFilter, warehouseFilter], () => void reload())

function money(value: number, currency: string | null): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [, supplierList, warehouseList] = await Promise.all([
      loadOrders(),
      api.listSuppliers().catch(() => []),
      api.listWarehouses().catch(() => []),
    ])
    suppliers.value = supplierList
    warehouses.value = warehouseList
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <div class="page-heading page-heading--actions">
    <div>
      <h2>{{ t('procurement.orders.title') }}</h2>
    </div>
    <RouterLink v-if="canCreate" class="profile-action profile-action--primary" to="/purchase-orders/new">
      <Plus :size="15" aria-hidden="true" />
      {{ t('procurement.orders.newOrder') }}
    </RouterLink>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">
      <RefreshCw :size="15" aria-hidden="true" />
      {{ t('procurement.common.retry') }}
    </button>
  </div>

  <template v-else>
    <div class="toolbar">
      <input v-model="search" class="search-input" type="search" :placeholder="t('procurement.orders.searchPlaceholder')">
      <select v-model="statusFilter" class="filter-select">
        <option value="">{{ t('procurement.orders.allStatuses') }}</option>
        <option v-for="status in statuses" :key="status" :value="status">
          {{ t(`procurement.status.${status}`) }}
        </option>
      </select>
      <select v-model="supplierFilter" class="filter-select">
        <option value="">{{ t('procurement.orders.allSuppliers') }}</option>
        <option v-for="supplier in suppliers" :key="supplier.id" :value="String(supplier.id)">{{ supplier.name }}</option>
      </select>
      <select v-model="warehouseFilter" class="filter-select">
        <option value="">{{ t('procurement.orders.allWarehouses') }}</option>
        <option v-for="warehouse in warehouses" :key="warehouse.id" :value="String(warehouse.id)">{{ warehouse.name }}</option>
      </select>
      <div class="summary">
        <span>{{ t('procurement.orders.count', { count: total }) }}</span>
      </div>
    </div>

    <div v-if="orders.length === 0" class="panel">
      <span class="empty-inline">{{ t('procurement.orders.empty') }}</span>
    </div>

    <template v-else>
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('procurement.orders.order') }}</th>
            <th>{{ t('procurement.orders.supplier') }}</th>
            <th>{{ t('procurement.orders.warehouse') }}</th>
            <th>{{ t('procurement.orders.status') }}</th>
            <th>{{ t('procurement.orders.expected') }}</th>
            <th class="cell-qty">{{ t('procurement.orders.total') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id">
            <td class="cell-name">
              #{{ order.id }}
              <small class="cell-sub">{{ formatDisplayDate(order.created_at) }}</small>
            </td>
            <td>{{ order.supplier_name || '—' }}</td>
            <td>{{ order.destination_warehouse_name || '—' }}</td>
            <td>
              <span :class="['status-badge', purchaseOrderStatusClass(order.status)]">
                {{ t(`procurement.status.${order.status}`) }}
              </span>
            </td>
            <td class="cell-date">{{ order.expected_date ? formatDisplayDate(order.expected_date) : '—' }}</td>
            <td class="cell-qty">{{ money(order.total_amount, order.currency_code) }}</td>
            <td>
              <RouterLink class="link-action" :to="`/purchase-orders/${order.id}`">
                {{ t('procurement.orders.open') }}
                <ArrowRight :size="13" aria-hidden="true" />
              </RouterLink>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="total > 0"
        :page="page"
        :total-pages="totalPages"
        :total="total"
        :per-page="perPage"
        @update:page="changePage"
        @update:per-page="changePerPage"
      />
    </template>
  </template>
</template>
